import React, {useState, useEffect} from "react";
import {Education} from "../types";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {EducationAPI} from "../services/API/EducationAPI";
import {Log} from "../utils/Log";
import {useTranslation} from "react-i18next";
import {Translate as TranslateIcon, Verified as VerifiedIcon} from "@mui/icons-material";
import {EducationalInstitutionDTO} from "../types/DTOs/EducationalInstitutionDTO";
import {EducationalInstitutionAPI} from "../services/API/EducationalInstitutionAPI";
import {EducationTranDTO} from "../types/DTOs/Translation/EducationTranDTO";
import {Translate} from "../utils/Translate";
import {TranslationAPI} from "../services/API/TranslationAPI";
import {SectionTypes} from "../utils/enums";

// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_LANG = 'original';

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────────────────────────────────────
interface EditEducationProps {
    item: Education | null;
    onSave: () => void;
    onCancel: () => void;
    open: boolean;
    onClose: () => void;
    /** Lingue già tradotte per questo elemento (chip nella lista) */
    translatedLanguages?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────
const fetchAvailableLanguages = async (): Promise<{ code: string; i18nKey: string }[]> => {
    return Translate.LanguageForCombo();
};

const fetchEducationTranslation = async (
    educationId: string,
    languageCode: string
): Promise<EducationTranDTO | null | undefined> => {
    const dataTran = await TranslationAPI.Instance<TranslationAPI>().getTranslatedItem(educationId, SectionTypes.EDUCATION, languageCode);
    if (dataTran === null || dataTran === undefined) return null;
    return dataTran as EducationTranDTO;
};

const saveEducationTranslation = async (data: EducationTranDTO): Promise<void> => {
    await TranslationAPI.Instance<TranslationAPI>().saveTranslateItem(data, SectionTypes.EDUCATION);
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────
export const EditEducation: React.FC<EditEducationProps> = ({
                                                                item,
                                                                onSave,
                                                                onCancel,
                                                                open,
                                                                onClose,
                                                                translatedLanguages = [],
                                                            }) => {
    // ── Stato originale ──
    const [education, setEducation] = useState<Education>({
        id: '',
        userId: '',
        institution: '',
        degree: '',
        startDate: new Date(),
        description: '',
        grade: '',
        institutionId: null,
        isVerified: false,
        createdAt: '',
        updatedAt: ''
    });

    // ── Stato istituzioni certificate ──
    const [institutions, setInstitutions] = useState<EducationalInstitutionDTO[]>([]);
    const [institutionsLoading, setInstitutionsLoading] = useState(false);

    // ── Stato traduzione ──
    const [availableLanguages, setAvailableLanguages] = useState<{ code: string; i18nKey: string }[]>([]);
    const [selectedLang, setSelectedLang] = useState<string>(DEFAULT_LANG);
    const [translation, setTranslation] = useState<EducationTranDTO>({
        id: '',
        educationId: '',
        degree: '',
        description: '',
    });
    const [translationLoading, setTranslationLoading] = useState(false);
    const [translationError, setTranslationError] = useState<string>('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const isTranslationMode = selectedLang !== DEFAULT_LANG;

    const { t } = useTranslation();

    // ─────────────────────────────────────────────────────────────────────────
    // EFFECTS
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            setSelectedLang(DEFAULT_LANG);
            setTranslationError('');
            setSaveSuccess(false);

            if (item === null) {
                setEducationDefault();
            } else {
                setEducation(item);
            }

            fetchAvailableLanguages()
                .then(setAvailableLanguages)
                .catch(() => {});

            setInstitutionsLoading(true);
            EducationalInstitutionAPI.Instance<EducationalInstitutionAPI>().getAll()
                .then(data => {
                    console.log('[EditEducation] Institutions loaded:', data);
                    setInstitutions(data);
                })
                .catch(err => {
                    console.error('[EditEducation] Failed to load institutions:', err);
                })
                .finally(() => setInstitutionsLoading(false));
        }
    }, [item, open]);

    useEffect(() => {
        if (!isTranslationMode || !education.id) return;

        setTranslationLoading(true);
        setTranslationError('');
        setSaveSuccess(false);

        fetchEducationTranslation(education.id, selectedLang)
            .then(data => {
                if (data) {
                    setTranslation(data);
                } else {
                    setTranslation({
                        id: '',
                        educationId: education.id,
                        degree: '',
                        description: '',
                    });
                }
            })
            .catch(() => setTranslationError(t('translation.errorGeneric')))
            .finally(() => setTranslationLoading(false));
    }, [selectedLang]);

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS
    // ─────────────────────────────────────────────────────────────────────────
    const setEducationDefault = () => {
        setEducation({
            id: '', userId: '', institution: '', degree: '',
            startDate: new Date(), description: '', grade: '',
            institutionId: null, isVerified: false,
            createdAt: '', updatedAt: ''
        });
    };

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        setEducation(prev => ({ ...prev, [name]: value }));
    };

    const handleTranslationInputChange = (event: any) => {
        const { name, value } = event.target;
        setTranslation(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (name: string, value: any) => {
        setEducation(prev => ({ ...prev, [name]: value }));
    };

    const handleLangChange = (e: SelectChangeEvent) => {
        setSelectedLang(e.target.value);
        setSaveSuccess(false);
    };

    // Salva originale
    const generateDescription = (degree: string, grade?: string): string => {
        if (grade) {
            return `${degree} — Voto: ${grade}`;
        }
        return degree;
    };

    const handleSaveItem = async () => {
        try {
            const toDateString = (date: Date | string | null | undefined): string | null => {
                if (!date) return null;
                return new Date(date).toISOString().split('T')[0];
            };

            const description = education.description || generateDescription(education.degree, education.grade);

            const payload: any = {
                degree: education.degree,
                startDate: toDateString(education.startDate)!,
                endDate: toDateString(education.endDate),
                description,
                grade: education.grade,
                institution: education.institution
            };

            if (education.institutionId) {
                payload.institutionId = education.institutionId;
            }

            if (!education.id) {
                await EducationAPI.Instance<EducationAPI>().createEducation(payload);
            } else {
                await EducationAPI.Instance<EducationAPI>().updateEducation(education.id, payload);
            }

            setEducationDefault();
            onSave();
        } catch (error) {
            console.error('Error saving education:', error);
        }
    };

    // Salva traduzione (non chiude la dialog)
    const handleSaveTranslation = async () => {
        setSaveLoading(true);
        setSaveSuccess(false);
        setTranslationError('');
        try {
            await saveEducationTranslation({
                ...translation,
                educationId: education.id,
            });
            setSaveSuccess(true);
        } catch {
            setTranslationError(t('translation.errorGeneric'));
        } finally {
            setSaveLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    const langLabel = availableLanguages.find(l => l.code === selectedLang)?.i18nKey ?? '';

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>

            {/* ── Titolo ── */}
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{t('educationDialog.title')}</span>
                    {isTranslationMode && (
                        <Chip
                            icon={<TranslateIcon />}
                            label={t(langLabel)}
                            color="primary"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            </DialogTitle>

            {/* ── Banda lingua ── */}
            {education.id && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>{t('translation.selectLanguageLabel')}</InputLabel>
                        <Select
                            value={selectedLang}
                            label={t('translation.selectLanguageLabel')}
                            onChange={handleLangChange}
                            startAdornment={<TranslateIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />}
                        >
                            <MenuItem value={DEFAULT_LANG}>
                                <em>{t('translation.originalLanguage')}</em>
                            </MenuItem>
                            <Divider />
                            {availableLanguages.map(lang => (
                                <MenuItem key={lang.code} value={lang.code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {t(lang.i18nKey)}
                                        {translatedLanguages.includes(lang.code) && (
                                            <Chip
                                                label={t('translation.alreadyTranslatedChip')}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isTranslationMode && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {translatedLanguages.includes(selectedLang)
                                ? t('translation.editModeExisting')
                                : t('translation.editModeNew')}
                        </Typography>
                    )}
                </Box>
            )}

            <DialogContent sx={{ pt: 2 }}>

                {translationLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {translationError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{translationError}</Alert>
                )}

                {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>{t('translation.saveSuccess')}</Alert>
                )}

                {!translationLoading && (
                    <>
                        {/* ── Istituto — NON traducibile ── */}
                        {education.isVerified && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                <Chip
                                    icon={<VerifiedIcon />}
                                    label={t('educationDialog.verified')}
                                    color="primary"
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        )}
                        <Tooltip
                            title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                            placement="top-start"
                        >
                            <Autocomplete
                                freeSolo
                                options={institutions.map(i => i.name)}
                                value={education.institution}
                                onChange={(_event, newValue) => {
                                    const name = typeof newValue === 'string' ? newValue : '';
                                    const match = institutions.find(
                                        i => i.name.toLowerCase() === name.toLowerCase()
                                    );
                                    if (match) {
                                        setEducation(prev => ({ ...prev, institutionId: match.id }));
                                    }
                                }}
                                onInputChange={(_event, newValue) => {
                                    setEducation(prev => ({ ...prev, institution: newValue }));
                                }}
                                disabled={isTranslationMode || education.isVerified}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        autoFocus
                                        margin="dense"
                                        label={t('educationDialog.institute')}
                                        variant="outlined"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {institutionsLoading ? <CircularProgress size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                )}
                            />
                        </Tooltip>

                        {/* ── Titolo rilasciato — traducibile ── */}
                        <TextField
                            name="degree"
                            margin="dense"
                            label={t('educationDialog.degree')}
                            fullWidth
                            variant="outlined"
                            value={isTranslationMode ? translation.degree : education.degree}
                            onChange={isTranslationMode ? handleTranslationInputChange : handleInputChange}
                            sx={{ mb: 2 }}
                        />

                        {/* ── Date — NON traducibili ── */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Tooltip
                                title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                                placement="top-start"
                            >
                                <span style={{ display: 'block', marginBottom: 8 }}>
                                    <DatePicker
                                        name="startDate"
                                        label={t('educationDialog.startDate')}
                                        views={['month', 'year']}
                                        format="MM/YYYY"
                                        value={dayjs(education.startDate)}
                                        onChange={(date) => handleDateChange('startDate', date)}
                                        disabled={isTranslationMode}
                                        sx={{ width: '100%' }}
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                                placement="top-start"
                            >
                                <span style={{ display: 'block', marginBottom: 8 }}>
                                    <DatePicker
                                        name="endDate"
                                        label={t('educationDialog.endDate')}
                                        views={['month', 'year']}
                                        format="MM/YYYY"
                                        value={education.endDate ? dayjs(education.endDate) : null}
                                        onChange={(date) => handleDateChange('endDate', date)}
                                        disabled={isTranslationMode}
                                        sx={{ width: '100%' }}
                                    />
                                </span>
                            </Tooltip>
                        </LocalizationProvider>

                        {/* ── Descrizione — traducibile ── */}
                        <TextField
                            name="description"
                            margin="dense"
                            label={t('educationDialog.description')}
                            fullWidth
                            variant="outlined"
                            value={isTranslationMode ? translation.description : education.description}
                            onChange={isTranslationMode ? handleTranslationInputChange : handleInputChange}
                            multiline
                            rows={3}
                            sx={{ mb: 2 }}
                        />

                        {/* ── Valutazione — NON traducibile ── */}
                        <Tooltip
                            title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                            placement="top-start"
                        >
                            <TextField
                                name="grade"
                                margin="dense"
                                label={t('educationDialog.grade')}
                                fullWidth
                                variant="outlined"
                                value={education.grade}
                                onChange={handleInputChange}
                                disabled={isTranslationMode}
                                sx={{ mb: 2 }}
                            />
                        </Tooltip>
                    </>
                )}

            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    {t('standardUse.undoBtn')}
                </Button>

                {isTranslationMode ? (
                    <Button
                        onClick={handleSaveTranslation}
                        variant="contained"
                        color="primary"
                        disabled={saveLoading || translationLoading}
                        startIcon={saveLoading
                            ? <CircularProgress size={16} color="inherit" />
                            : <TranslateIcon />
                        }
                    >
                        {saveLoading
                            ? t('translation.savingButton')
                            : t('translation.saveTranslationButton', { lang: t(langLabel) })}
                    </Button>
                ) : (
                    <Button onClick={handleSaveItem} variant="contained">
                        {t('standardUse.saveBtn')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};